import { CreditCard } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { formatMomentDate } from '../../../../utils/dateHelper';
import UserNetworkingModal from '../../../../components/modals/UserNetworkingModal';
import AssignPlanModal from '../../../../components/modals/AssignPlanModal';
import { VerifiedIcon } from '../../../../assets/icons';
import CommonButton from '../../../../components/commonButton';
import { manualVerifyUserIdentity } from '../../../../redux/slice/allUsersSlice';
import { TabCard } from '../../../../components/custom-card/Cards';
import { useToast } from '../../../../context/TostContext';
import StatusChip from '../../../../components/statusChip/StatusChip';
import ConfirmModal from '../../../../components/withModal/ConfirmModal';

const Overview = ({ onEditAbout }) => {
    const {id} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { userDetail } = useSelector((state) => state.allUsers);
    const dispatch = useDispatch();
    const [showNetworkingModal, setShowNetworkingModal] = useState(false);
    const [networkingType, setNetworkingType] = useState("");
    const [showAssignPlanModal, setShowAssignPlanModal] = useState(false);
    const toast = useToast();
    const bioRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const [confirmModalConfig, setConfirmModalConfig] = useState({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: () => { },
        confirmText: "Yes",
        cancelText: "No"
    });

    // Sync networking modal state with search params
    useEffect(() => {
        const showNetworking = searchParams.get("showNetworking") === "true";
        const type = searchParams.get("networkingType");
        if (showNetworking && type) {
            setNetworkingType(type);
            setShowNetworkingModal(true);
        } else {
            setShowNetworkingModal(false);
            setNetworkingType("");
        }
    }, [searchParams]);

    useEffect(() => {
        setIsExpanded(false);
    }, [userDetail?.profile?.about]);

    useEffect(() => {
        const checkOverflow = () => {
            if (!isExpanded && bioRef.current) {
                const hasOverflow = bioRef.current.scrollHeight > bioRef.current.clientHeight;
                setIsClamped(hasOverflow);
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [userDetail?.profile?.about, isExpanded]);

    const handleVerifyIdentity = () => {
        if (!userDetail) return;
        setConfirmModalConfig({
            isOpen: true,
            title: "Verify Identity",
            message: "Are you sure you want to verify this user's identity?",
            confirmText: "Verify",
            cancelText: "Cancel",
            onConfirm: () => {
                dispatch(manualVerifyUserIdentity(userDetail.id))
                    .unwrap()
                    .then(() => {
                        toast.success("User identity verified successfully");
                    })
                    .catch((error) => {
                        toast.error(error?.message || "Failed to verify identity");
                    });
            }
        });
    };

    const sub = userDetail?.subscription;
    const isFree = !sub || sub.plan_type === "free";

    const getProgressWidth = () => {
        if (isFree || !sub.end_date || !sub.start_date) return "100%";
        const start = new Date(sub.start_date);
        const end = new Date(sub.end_date);
        const today = new Date();
        const total = end - start;
        if (total <= 0) return "100%";
        const elapsed = today - start;
        const percentage = Math.min(100, Math.max(0, (elapsed / total) * 100));
        return `${percentage}%`;
    };

    const renderPaymentMethod = () => {
        const payment = sub?.payment;
        if (!payment) return "None";
        const method = payment.payment_method;
        if (!method) return "Online";
        if (typeof method === 'string') {
            return method;
        }
        if (typeof method === 'object') {
            if (method.card) {
                const card = method.card;
                const network = card.card_network || card.channel || "Card";
                const last4 = card.card_number ? card.card_number.slice(-4) : "";
                return last4 ? `${network} •••• ${last4}` : network;
            }
            if (method.upi) {
                return "UPI";
            }
            if (method.netbanking) {
                return method.netbanking.bank_name || "Netbanking";
            }
            if (method.wallet) {
                return method.wallet.wallet_name || "Wallet";
            }
        }
        return "Online";
    };

    const getAmountFormatted = () => {
        const payment = sub?.payment;
        if (!payment || !payment.payable_amount) return "₹0.00";
        const amount = parseFloat(payment.payable_amount);
        return `₹${amount.toFixed(2)}`;
    };

    const formatInterval = (interval) => {
        if (!interval) return "N/A";
        return interval.charAt(0).toUpperCase() + interval.slice(1);
    };

    const getPlanPeriodText = () => {
        if (isFree || !sub?.interval) return "";
        switch (sub.interval) {
            case "daily": return "/ day";
            case "weekly": return "/ week";
            case "monthly": return "/ month";
            case "yearly": return "/ year";
            case "lifetime": return "";
            default: return `/ ${sub.interval}`;
        }
    };

    const handleCardClick = (key) => {
        if (["connections", "followers", "following"].includes(key)) {
            const updated = new URLSearchParams(searchParams);
            updated.set("showNetworking", "true");
            updated.set("networkingType", key);
            setSearchParams(updated);
        } else if (["companies", "products", "marketplace-listings", "requirements"].includes(key)) {
            const updated = new URLSearchParams(searchParams);
            updated.set("tab", "profile");
            updated.set("profile-tabs", key);
            setSearchParams(updated);
        } else {
            const updated = new URLSearchParams(searchParams);
            updated.set("tab", key);
            setSearchParams(updated);
        }
    };

    const cardData = [
        {
            key: "connections",
            title: "Connections",
            count: userDetail?.connections_count || 0,
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
            ),
            bg: "#eef4fe",
            color: "#3b82f6"
        },
        {
            key: "followers",
            title: "Followers",
            count: userDetail?.followers_count || 0,
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <line x1="19" y1="8" x2="19" y2="14" />
                    <line x1="16" y1="11" x2="22" y2="11" />
                </svg>
            ),
            bg: "#fff7ed",
            color: "#f97316"
        },
        {
            key: "following",
            title: "Following",
            count: userDetail?.following_count || 0,
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
            ),
            bg: "#f0fdf4",
            color: "#22c55e"
        },
        {
            key: "companies",
            title: "Companies",
            count: userDetail?.company_count || 0,
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
            ),
            bg: "#fff7ed",
            color: "#f97316"
        },
        {
            key: "posts",
            title: "Posts",
            count: userDetail?.post_count || 0,
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
            ),
            bg: "#faf5ff",
            color: "#a855f7"
        },
        {
            key: "products",
            title: "Products",
            count: userDetail?.product_count || userDetail?.products?.length || 0,
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
            ),
            bg: "#f0fdf4",
            color: "#22c55e"
        },
        {
            key: "marketplace-listings",
            title: "Listings",
            count: userDetail?.listings_count || userDetail?.marketplace_listings?.length || 0,
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
            ),
            bg: "#ecfeff",
            color: "#06b6d4"
        },
        {
            key: "requirements",
            title: "Requirements",
            count: userDetail?.requirements_count || userDetail?.requirements?.length || 0,
            icon: (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                </svg>
            ),
            bg: "#fff1f2",
            color: "#f43f5e"
        }
    ];

    return (
        <div className='overview-tab'>
            <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: "repeat(8, 1fr)" }}>
                {cardData.map((card) => (
                    <TabCard key={card.key} title={card.title} count={card.count} onClick={() => handleCardClick(card.key)} icon={card.icon} iconBg={card.bg} iconColor={card.color} />
                ))}
            </div>
            {/* details card */}
            <div className="grid gap-3 mt-4 align-items-start">
                {/* About Card */}
                <Card className="overview-details-card g-col-3 border-0 shadow-sm">
                    <Card.Body className="p-4 d-flex flex-column h-100">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h5 className="overview-details-card-title fw-bold mb-0">About</h5>

                        </div>
                        <p
                            ref={bioRef}
                            className={`overview-details-bio mb-4 ${!isExpanded ? 'collapsed' : ''} ${isClamped ? 'has-toggle' : ''}`}
                        >
                            {userDetail?.profile?.about || "No bio added yet."}
                        </p>
                        {isClamped && (
                            <button
                                type="button"
                                className="bio-toggle-btn"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded ? "Show less" : "Show more"}
                            </button>
                        )}
                        <div className="overview-details-list flex-grow-1">
                            <div className="overview-details-row">
                                <span className="overview-details-label">Profession</span>
                                <span className="overview-details-value text-dark">{userDetail?.profile?.profession?.name || "-"}</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Headline</span>
                                <span className="overview-details-value fw-semibold text-dark">{userDetail?.profile?.headline || "-"}</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Website</span>
                                <span className="overview-details-value">
                                    {userDetail?.profile?.website ? (
                                        <a href={userDetail.profile.website} target="_blank" rel="noopener noreferrer" className="overview-details-link">
                                            {userDetail.profile.website}
                                        </a>
                                    ) : "-"}
                                </span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Whatsapp</span>
                                <span className="overview-details-value text-dark">{userDetail?.profile?.whatsapp_number || "-"}</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Address</span>
                                <span className="overview-details-value text-dark">{userDetail?.profile?.address || "-"}</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">City</span>
                                <span className="overview-details-value text-dark">{userDetail?.profile?.city?.name || "-"}</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">State</span>
                                <span className="overview-details-value text-dark">{userDetail?.profile?.city?.state?.name || "-"}</span>
                            </div>
                            {/* <div className="overview-details-row">
                                <span className="overview-details-label">Business</span>
                                <span className="overview-details-value text-muted">{userDetail?.business?.name || "-"}</span>
                            </div> */}
                            <div className="overview-details-row align-items-start">
                                <span className="overview-details-label pt-1">Categories</span>
                                <div className="overview-details-value d-flex flex-wrap gap-1">
                                    {userDetail?.categories?.length > 0 ? (
                                        userDetail.categories.map((cat, i) => (
                                            <span key={i} className="category-badge">{cat.name || cat}</span>
                                        ))
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Account Details Card */}
                {/* <Card className="overview-details-card g-col-3 border-0 shadow-sm">
                    <Card.Body className="p-4 d-flex flex-column h-100">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h5 className="overview-details-card-title fw-bold mb-0">Account Details</h5>
                        </div>
                        <div className="overview-details-list flex-grow-1">
                            <div className="overview-details-row">
                                <span className="overview-details-label">Email</span>
                                <div className="overview-details-value d-flex align-items-center">
                                    <span className="text-dark">{userDetail?.email || "-"}</span>
                                    {userDetail?.email_verified ? (
                                        <span className="badge-verified">
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            Verified
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Mobile</span>
                                <div className="overview-details-value d-flex align-items-center">
                                    <span className="text-dark">{userDetail?.mobile_number || "-"}</span>
                                    {userDetail?.mobile_verified ? (
                                        <span className="badge-verified">
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            Verified
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Gender</span>
                                <span className="overview-details-value text-dark">{userDetail?.profile?.gender || "-"}</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Date of Birth</span>
                                <span className="overview-details-value text-dark">
                                    {userDetail?.profile?.dob ? formatMomentDate(userDetail.profile.dob, "DD MMM YYYY") : "-"}
                                </span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Language</span>
                                <span className="overview-details-value text-dark">{userDetail?.profile?.language || "-"}</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Account Status</span>
                                <div className="overview-details-value">
                                    <span className="badge-active-status">{userDetail?.status || "-"}</span>
                                </div>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Email Notifications</span>
                                <span className="overview-details-value text-dark">Enabled</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">SMS Notifications</span>
                                <span className="overview-details-value text-dark">Enabled</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Two Factor Auth</span>
                                <span className="overview-details-value text-dark">Disabled</span>
                            </div>
                        </div>
                    </Card.Body>
                </Card> */}

                {/* Verification Summary Card */}
                <Card className="overview-details-card g-col-3 border-0 shadow-sm">
                    <Card.Body className="p-4 d-flex flex-column h-100">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h5 className="overview-details-card-title fw-bold mb-0">Verification Summary</h5>
                            <a
                                href="#view-all-verifications"
                                className="overview-details-view-all-link fw-semibold"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setSearchParams({ tab: 'verifications' });
                                }}
                            >
                                View All
                            </a>
                        </div>
                        <div className="verification-rows-container flex-grow-1">
                            <div className="verification-row">
                                <span className="verification-label">Email Verification</span>
                                <div className={`verification-value-wrapper ${userDetail?.is_email_verified ? 'text-success' : 'text-danger'}`}>
                                    <span className="fw-semibold me-2">
                                        {userDetail?.is_email_verified ? 'Verified' : 'Not Verified'}
                                    </span>
                                    {userDetail?.is_email_verified ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-success">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="16 9 11 14 8 11" />
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-danger">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="15" y1="9" x2="9" y2="15" />
                                            <line x1="9" y1="9" x2="15" y2="15" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                            <div className="verification-row">
                                <span className="verification-label">Mobile Verification</span>
                                <div className={`verification-value-wrapper ${userDetail?.is_mobile_verified ? 'text-success' : 'text-danger'}`}>
                                    <span className="fw-semibold me-2">
                                        {userDetail?.is_mobile_verified ? 'Verified' : 'Not Verified'}
                                    </span>
                                    {userDetail?.is_mobile_verified ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-success">
                                            <circle cx="12" cy="12" r="10" />
                                            <polyline points="16 9 11 14 8 11" />
                                        </svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-danger">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="15" y1="9" x2="9" y2="15" />
                                            <line x1="9" y1="9" x2="15" y2="15" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                            <div className="verification-row">
                                <span className="verification-label">Identity Verification</span>
                                <div className="d-flex align-items-center gap-2">
                                    <div className={`verification-value-wrapper ${userDetail?.identity_verified ? 'text-success' : 'text-danger'}`}>
                                        <span className="fw-semibold me-2">
                                            {userDetail?.identity_verified ? 'Verified' : 'Not Verified'}
                                        </span>
                                        {userDetail?.identity_verified ? (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-success">
                                                <circle cx="12" cy="12" r="10" />
                                                <polyline points="16 9 11 14 8 11" />
                                            </svg>
                                        ) : (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-danger">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="15" y1="9" x2="9" y2="15" />
                                                <line x1="9" y1="9" x2="15" y2="15" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {!userDetail?.identity_verified && (
                            <CommonButton
                                title="Verify Identity"
                                icon={<VerifiedIcon size={16} />}
                                variant="success"
                                className="w-100 mt-3"
                                onClick={handleVerifyIdentity}
                            />
                        )}
                    </Card.Body>
                </Card>
                {/* Subscription Summary Card */}
                <Card className="overview-details-card subscription-card g-col-3 border-0 shadow-sm">
                    <Card.Body className="p-4 d-flex flex-column h-100">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h5 className="overview-details-card-title fw-bold mb-0">Subscription Summary</h5>
                            <StatusChip bg={"success"} title={isFree ? "Free Tier" : sub?.status} />
                        </div>

                        {/* Premium Plan highlight box */}
                        <div className="subscription-plan-box mb-4 p-3 rounded-3 position-relative overflow-hidden">
                            <div className="d-flex align-items-center justify-content-between position-relative z-1">
                                <div>
                                    <span className="plan-label text-uppercase fw-bold opacity-75">Current Plan</span>
                                    <h4 className="plan-name fw-extrabold mb-0 mt-1">{sub?.plan_name || "Free"}</h4>
                                </div>
                                <div className="text-end">
                                    <h4 className="plan-price fw-extrabold mb-0">{isFree ? "₹0.00" : getAmountFormatted()}</h4>
                                    <span className="plan-period opacity-75">{getPlanPeriodText()}</span>
                                </div>
                            </div>
                            <div className="plan-glow position-absolute"></div>
                        </div>

                        {/* Progress limit bar */}
                        <div className="subscription-usage mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-1 text-xs">
                                <span className="usage-label fw-semibold text-muted">Plan Validity</span>
                                <span className="usage-value fw-bold text-dark">{isFree ? "Unlimited" : "Active"}</span>
                            </div>
                            <div className="progress usage-progress-bg" style={{ height: "6px" }}>
                                <div
                                    className="progress-bar subscription-progress-bar rounded-pill"
                                    role="progressbar"
                                    style={{ width: getProgressWidth() }}
                                    aria-valuenow={parseFloat(getProgressWidth())}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                            <span className="usage-renewal-text text-muted mt-1 d-block">
                                Expiry Date: {(!isFree && sub?.end_date) ? formatMomentDate(sub.end_date, "DD MMM YYYY") : "Never"}
                            </span>
                        </div>

                        {/* Details List */}
                        <div className="overview-details-list flex-grow-1">
                            <div className="overview-details-row">
                                <span className="overview-details-label">Billing Cycle</span>
                                <span className="overview-details-value text-dark fw-semibold">{isFree ? "N/A" : formatInterval(sub?.interval)}</span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Payment Method</span>
                                <span className="overview-details-value text-dark d-flex align-items-center gap-1">
                                    <CreditCard size={16} />
                                    {renderPaymentMethod()}
                                </span>
                            </div>
                            <div className="overview-details-row">
                                <span className="overview-details-label">Amount Paid</span>
                                <span className="overview-details-value text-dark fw-semibold">{isFree ? "₹0.00" : getAmountFormatted()}</span>
                            </div>
                        </div>

                        {/* Manage Subscription CTA */}
                        {isFree ? (
                            <button
                                className="manage-subscription-btn w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                                onClick={() => setShowAssignPlanModal(true)}
                            >
                                <span>Assign Plan</span>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        ) : (
                            <button
                                className="manage-subscription-btn w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
                                onClick={() => setSearchParams({ tab: 'subscriptions' })}
                            >
                                <span>View All</span>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        )}
                    </Card.Body>
                </Card>
                {/* <Card className=" g-col-12 border-0 shadow-sm "> ... </Card> */}
            </div>

            {/* User Networking Modal */}
            <UserNetworkingModal
                isOpen={showNetworkingModal}
                onClose={() => {
                    const updated = new URLSearchParams(searchParams);
                    updated.delete("showNetworking");
                    updated.delete("networkingType");
                    setSearchParams(updated);
                }}
                userId={id || userDetail?.id}
                type={networkingType}
                className="networking-modal"
            />

            {/* Assign Plan Modal */}
            <AssignPlanModal
                isOpen={showAssignPlanModal}
                onClose={() => setShowAssignPlanModal(false)}
                userId={id || userDetail?.id}
                title="ASSIGN PLAN"
                className="tp-file-modal"
            />
            <ConfirmModal
                isOpen={confirmModalConfig.isOpen}
                onClose={() => setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }))}
                title={confirmModalConfig.title}
                message={confirmModalConfig.message}
                onConfirm={confirmModalConfig.onConfirm}
                confirmText={confirmModalConfig.confirmText}
                cancelText={confirmModalConfig.cancelText}
                className='confirm-modal'
                size='sm'
            />
        </div>
    );
};

export default Overview;